import { Controller, useForm } from "react-hook-form";
import {
  Description,
  ErrorMessage,
  Field,
  Label,
} from "../../components/catalyst/fieldset";
import { Input } from "../../components/catalyst/input";
import { formatDate, showDate } from "../../utils/dateUtils";
import { Heading } from "../../components/Heading";
import { CheckIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { FormSection } from "../../components/FormSection";
import { ReadOnlyFormElement } from "../../components/ReadOnlyFormElement";
import useAuthStore from "../../store/authStore";
import { Season } from "../../models/leagueInterfaces";
import {
  Listbox,
  ListboxLabel,
  ListboxOption,
} from "../../components/catalyst/listbox";
import { Checkbox, CheckboxField } from "../../components/catalyst/checkbox";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotificationsStore } from "../../store/notificationsStore";
import { Busy } from "../../components/Busy";
import { useConfirmationDialog } from "../../components/ConfirmDialog";
import { Button } from "../../components/catalyst/button";
import Avatar from "../../components/Avatar";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const RegisterSeason = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
    watch,
    clearErrors,
    setError,
  } = useForm();

  const { currentUser } = useAuthStore();
  const {
    notifications,
    registerForSeason,
    isBusy,
    userRegistrationForSeason,
  } = useNotificationsStore();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const notificationId = queryParams.get("id");

  const [currentSeason, setCurrentSeason] = useState<Season | undefined>();

  const [isPlayer, setIsPlayer] = useState(true);
  const [isTeamOwner, setIsTeamOwner] = useState(false);
  const [filePreviewTeamLogo, setFilePreviewTeamLogo] = useState<any>(null);
  const [filePreviewHCICert, setFilePreviewHCICert] = useState<any>(null);
  const [selectedFileHCICert, setSelectedFileHCICert] = useState<any>(null);

  const registrationType = watch("registrationType");
  const isSenior = watch("ageCategory") === "Senior";
  const homeClub = watch("homeClub");
  const navigate = useNavigate();
  const { openDialog } = useConfirmationDialog();

  useEffect(() => {
    if (userRegistrationForSeason) {
      console.log("userRegistrationForSeason:", userRegistrationForSeason);

      // Check if homeClub is in the predefined list
      const isValidHomeClub = ["HGA", "BoulderHills"].includes(userRegistrationForSeason.homeClub);

      // Set form values
      reset({
        registrationType: userRegistrationForSeason.role,
        teamName: userRegistrationForSeason.teamName,
        teamLogo: userRegistrationForSeason.teamLogo,
        gender: userRegistrationForSeason.gender,
        ageCategory: userRegistrationForSeason.ageCategory,
        yearOfBirth: userRegistrationForSeason.yearOfBirth,
        leaguesPlayed: userRegistrationForSeason.leaguesPlayed,
        tshirtSize: userRegistrationForSeason.tshirtSize,
        playerHandicap: userRegistrationForSeason.playerHandicap,
        homeClub: isValidHomeClub ? userRegistrationForSeason.homeClub : "Other",
        homeClubName: isValidHomeClub ? "" : userRegistrationForSeason.homeClub,
        handicapCertificate: userRegistrationForSeason.handicapCertificate,
        preferredDates: userRegistrationForSeason.preferredDates,
        playerId: userRegistrationForSeason.playerId,
      });

      // Set file previews
      if (userRegistrationForSeason.teamLogo) {
        setFilePreviewTeamLogo(`/api/static/images/${userRegistrationForSeason.teamLogo}`);
      }
      
      if (userRegistrationForSeason.handicapCertificate) {
        const fileUrl = `/api/static/images/${userRegistrationForSeason.handicapCertificate}`;
        setFilePreviewHCICert(fileUrl);
        // Create a dummy File object to set selectedFileHCICert
        const dummyFile = new File([""], userRegistrationForSeason.handicapCertificate, {
          type: userRegistrationForSeason.handicapCertificate.toLowerCase().endsWith('.pdf') 
            ? 'application/pdf' 
            : 'image/*'
        });
        setSelectedFileHCICert(dummyFile);
      }
    }
  }, [userRegistrationForSeason, reset]);

  useEffect(() => {
    if (notificationId) {
      const season = notifications.find(
        (n) => n.notificationId === notificationId
      )?.season;
      if (season) {
        setCurrentSeason(season);
      } else {
        navigate("/");
      }
    } else {
      navigate("/");
    }
  }, [notificationId]);

  useEffect(() => {
    if (!userRegistrationForSeason) {
      reset({
        preferredDates: [],
        registrationType: "player",
      });
    }
  }, [reset]);

  const onSubmit = async (data: any) => {
    console.log("raw data:", data);
    const registerData = {
      ...data,
      playerHandicap: data.playerHandicap ? data.playerHandicap.toString() : "",
      homeClub: homeClub === "Other" ? data.homeClubName : homeClub,
      teamLogo: typeof data.teamLogo === "string" ? data.teamLogo : data.teamLogo ? data.teamLogo[0] : null,
      handicapCertificate: typeof data.handicapCertificate === "string" ? data.handicapCertificate : data.handicapCertificate ? data.handicapCertificate[0] : null,
      seasonId: currentSeason?.id,
      userEmail: currentUser?.email,
    };

    console.log("being sent to servcie", registerData);
    const result = await registerForSeason(registerData);
    if (result) {
      navigate("/");
    }
  };

  const onCancel = () => {
    const isEditing = watch('playerId') ? true : false;
    
    openDialog({
      title: isEditing 
        ? "Are you sure you want to cancel editing?" 
        : "Are you sure you want to cancel registration?",
      message: isEditing
        ? "Any changes made will be lost. Are you sure you want to cancel?"
        : "Registration form data will be lost. Are you sure you want to cancel?",
      confirmBtnText: isEditing ? "Yes, Cancel Editing" : "Yes, Cancel Registration",
      cancelBtnText: isEditing ? "No, Continue Editing" : "No, Continue Registration",
      onConfirm: () => {
        reset({
          preferredDates: [],
          registrationType: "player",
        });
        navigate("/");
      },
    });
  };

  const seasonDatesString = (season: Season | undefined) => {
    if (
      season &&
      season.seasonStartAndEndDates &&
      season.seasonStartAndEndDates.length > 0
    ) {
      return `${formatDate(season.seasonStartAndEndDates[0])} - ${formatDate(
        season.seasonStartAndEndDates[1]
      )}`;
    }
    return "";
  };

  useEffect(() => {
    if (
      registrationType === "player" ||
      registrationType === "playingTeamOwner"
    ) {
      setIsPlayer(true);
    } else {
      setIsPlayer(false);
    }

    if (
      registrationType === "playingTeamOwner" ||
      registrationType === "nonPlayingTeamOwner"
    ) {
      setIsTeamOwner(true);
    } else {
      setIsTeamOwner(false);
    }
  }, [registrationType]);

  const handleFileChangeTeamLogo = (event: any) => {
    const sf = event.target.files[0];

    if (sf) {
      // Validate file size
      if (sf.size > MAX_FILE_SIZE) {
        setError("teamLogo", {
          type: "manual",
          message: "File size exceeds 10MB. Please choose a smaller file.",
        });
        setFilePreviewTeamLogo(null); // Reset preview
      } else {
        clearErrors("teamLogo"); // Clear the error if the file is valid
        const fileURL = URL.createObjectURL(sf);
        setFilePreviewTeamLogo(fileURL); // Set the preview URL
      }
    }
  };
  const handleFileChangeHCICert = (event: any) => {
    const sf = event.target.files[0];

    if (sf) {
      setSelectedFileHCICert(sf);
      // Validate file size
      if (sf.size > MAX_FILE_SIZE) {
        setError("handicapCertificate", {
          type: "manual",
          message: "File size exceeds 10MB. Please choose a smaller file.",
        });
        setFilePreviewHCICert(null); // Reset preview
      } else {
        clearErrors("handicapCertificate"); // Clear the error if the file is valid
        const fileURL = URL.createObjectURL(sf);
        setFilePreviewHCICert(fileURL); // Set the preview URL
      }
    }
  };

  return (
    <>
      <form>
        <Heading text="Register for the season">
          <div className="flex gap-4">
            <Button color="red" onClick={onCancel}>
              <XCircleIcon className="h-6 w-6" />
              Cancel
            </Button>
            <Button color="green" onClick={handleSubmit(onSubmit)}>
              <CheckIcon className="h-6 w-6" />
              Save
            </Button>
          </div>
        </Heading>

        <FormSection
          formDescription="Highlights of the season and registration details."
          formHeading="Season Details"
        >
          <div className="flex flex-col gap-4">
            <div className="col-span-2">
              <ReadOnlyFormElement
                label="Season dates:"
                value={seasonDatesString(currentSeason)}
              />
            </div>

            <div className="col-span-2">
              <ReadOnlyFormElement
                label="Golf Courses:"
                value={currentSeason ? currentSeason.golfCoursesAsText : ""}
              />
            </div>
          </div>
        </FormSection>

        <div className="hidden">
          <FormSection
            formDescription="Are you registering as a player or creating a team?"
            formHeading="Registration Type"
          >
            <div className="flex flex-col gap-4">
              <div className="col-span-3">
                <Field>
                  <Label>Registering as</Label>
                  <Description>
                    Choose between the options of registration types:
                    <br />
                    <span>
                      Core Members are to register as 'Golfer interested in
                      playing the season' and they will be provided an invite
                      link by the Team Owner after the team is registered.
                    </span>
                    <br />
                    <span>
                      If the team has multiple owners, only one owner can
                      register the team and send invite links to others.
                    </span>
                    <br />
                    <span>
                      If you are not creating a team, choose 'Golfer interested
                      in playing the season'.
                    </span>
                  </Description>
                  <Controller
                    name="registrationType"
                    control={control}
                    rules={{ required: "Registration type is required" }}
                    render={({ field }) => (
                      <Listbox
                        name="registrationType"
                        value={field.value}
                        invalid={!!errors.registrationType}
                        onChange={(e) => setValue("registrationType", e)}
                      >
                        <ListboxOption value="player">
                          <ListboxLabel>
                            Golfer interested in playing the season.
                          </ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="playingTeamOwner">
                          <ListboxLabel>
                            Playing Team Owner creating a new team.
                          </ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="nonPlayingTeamOwner">
                          <ListboxLabel>
                            Non Playing Team Owner creating a new team.
                          </ListboxLabel>
                        </ListboxOption>
                      </Listbox>
                    )}
                  />
                  {errors.registrationType && (
                    <ErrorMessage>
                      {errors.registrationType?.message as string}
                    </ErrorMessage>
                  )}
                </Field>
              </div>

              {isTeamOwner && (
                <>
                  <div className="col-span-2">
                    <Field>
                      <Label>Team Name</Label>
                      <Description>
                        Enter a unique team name for the season.
                      </Description>
                      <Input
                        type="text"
                        {...register("teamName", {
                          required: "Team name is required",
                        })}
                      />
                      {errors.teamName && (
                        <ErrorMessage>
                          {errors.teamName.message as string}
                        </ErrorMessage>
                      )}
                    </Field>
                  </div>

                  <div className="col-span-2">
                    <Field>
                      <Label>Team logo or team profile picture</Label>
                      <Description>Upload your team logo or image.</Description>
                      <div className="flex gap-5 justify-center items-center">
                        <input
                          type="file"
                          {...register("teamLogo", {})}
                          onChange={handleFileChangeTeamLogo}
                          accept="image/*"
                          style={{ height: "39px", width: "109px" }}
                          className="block w-full text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-500"
                        />
                        <div style={{ width: "200px", height: "60px" }}>
                          {filePreviewTeamLogo && (
                            <img
                              src={filePreviewTeamLogo}
                              alt="Team Logo"
                              style={{ width: "180px", height: "100px" }}
                            />
                          )}
                        </div>
                      </div>
                      {errors.teamLogo && (
                        <ErrorMessage>
                          {errors.teamLogo.message as string}
                        </ErrorMessage>
                      )}
                    </Field>
                  </div>
                </>
              )}
            </div>
          </FormSection>
        </div>
        <FormSection
          className="mt-1"
          formDescription="View or edit user profile details from user settings section."
          formHeading={isPlayer ? "Player Details" : "Team Owner Details"}
        >
          <>
            <div className="grid grid-cols-6 gap-x-3 gap-y-8">
              <div className="col-span-2">
                <Avatar
                  firstName={currentUser?.firstName ?? ''}
                  lastName={currentUser?.lastName ?? ''}
                  imageUrl={`/api/static/images/${currentUser?.profilePicture}`}
                  className="w-20 h-20"
                />
              </div>
              <div className="col-span-2">
                <ReadOnlyFormElement
                  label="Name"
                  value={
                    currentUser
                      ? `${currentUser.title} ${currentUser.firstName} ${currentUser.lastName}`
                      : ""
                  }
                />
              </div>

              <div className="col-span-2">
                <ReadOnlyFormElement
                  label="Phone Number"
                  value={currentUser?.phone}
                />
              </div>

              <div className="col-span-2">
                <ReadOnlyFormElement
                  label="Email (username)"
                  value={currentUser?.email}
                />
              </div>
            </div>
            {isPlayer && (
              <div className="grid grid-cols-4 gap-x-3 gap-y-8 mt-6">
                <div className="col-span-6 sm:col-span-3">
                  <Field>
                    <Label>Gender</Label>
                    <Controller
                      name="gender"
                      control={control}
                      rules={{ required: "Gender is required" }}
                      render={({ field }) => (
                        <Listbox
                          name="gender"
                          value={field.value}
                          invalid={!!errors.gender}
                          onChange={(e) => setValue("gender", e)}
                        >
                          <ListboxOption value="">
                            <ListboxLabel></ListboxLabel>
                          </ListboxOption>
                          <ListboxOption value="Male">
                            <ListboxLabel>Male</ListboxLabel>
                          </ListboxOption>
                          <ListboxOption value="Female">
                            <ListboxLabel>Female</ListboxLabel>
                          </ListboxOption>
                        </Listbox>
                      )}
                    />
                    {errors.gender && (
                      <ErrorMessage>
                        {errors.gender?.message as string}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <Field>
                    <Label>Age category</Label>
                    <Description>
                      Age as of start date of the season.
                    </Description>
                    <Controller
                      name="ageCategory"
                      control={control}
                      rules={{ required: "Age category is required" }}
                      render={({ field }) => (
                        <Listbox
                          name="ageCategory"
                          value={field.value}
                          invalid={!!errors.ageCategory}
                          onChange={(e) => setValue("ageCategory", e)}
                        >
                          <ListboxOption value="NotSenior">
                            <ListboxLabel>Below 65</ListboxLabel>
                          </ListboxOption>
                          <ListboxOption value="Senior">
                            <ListboxLabel>Above 65</ListboxLabel>
                          </ListboxOption>
                        </Listbox>
                      )}
                    />
                    {errors.ageCategory && (
                      <ErrorMessage>
                        {errors.ageCategory?.message as string}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>

                {isSenior && (
                  <div className="col-span-6 sm:col-span-3">
                    <Field>
                      <Label>Year of birth</Label>
                      <Description>Please provide your birth year.</Description>
                      <Controller
                        name="yearOfBirth"
                        control={control}
                        render={({ field }) => (
                          <Listbox
                            name="yearOfBirth"
                            value={field.value}
                            invalid={!!errors.yearOfBirth}
                            onChange={(e) => setValue("yearOfBirth", e)}
                          >
                            <ListboxOption value="">
                              <ListboxLabel></ListboxLabel>
                            </ListboxOption>
                            {[...Array(56).keys()].map((year) => (
                              <ListboxOption
                                key={year}
                                value={new Date().getFullYear() - 65 - year}
                              >
                                <ListboxLabel>
                                  {new Date().getFullYear() - 65 - year}
                                </ListboxLabel>
                              </ListboxOption>
                            ))}
                          </Listbox>
                        )}
                      />
                      {errors.yearOfBirth && (
                        <ErrorMessage>
                          {errors.yearOfBirth?.message as string}
                        </ErrorMessage>
                      )}
                    </Field>
                  </div>
                )}
                <div className="col-span-6 sm:col-span-3">
                  <Field>
                    <Label>Leagues played</Label>
                    <Description>
                      Please provide names of leagues played. None or NA if
                      none.
                    </Description>
                    <Input
                      type="text"
                      {...register("leaguesPlayed", {
                        required: "Leagues played is required",
                      })}
                    />
                    {errors.leaguesPlayed && (
                      <ErrorMessage>
                        {errors.leaguesPlayed.message as string}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </div>
            )}
            <div className="grid grid-cols-6 gap-x-3 gap-10 mt-6">
              <div className="col-span-6 sm:col-span-3">
                <Field>
                  <Label>T-Shirt size - Indian Sizes</Label>
                  <Description>Please select your T-shirt size</Description>
                  <Controller
                    name="tshirtSize"
                    control={control}
                    rules={{ required: "T-shirt size is required" }}
                    render={({ field }) => (
                      <Listbox
                        name="tshirtSize"
                        value={field.value}
                        invalid={!!errors.tshirtSize}
                        onChange={(e) => setValue("tshirtSize", e)}
                      >
                        <ListboxOption value="xs">
                          <ListboxLabel>X Small</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="s">
                          <ListboxLabel>Small</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="m">
                          <ListboxLabel>Medium</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="l">
                          <ListboxLabel>Large</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="xl">
                          <ListboxLabel>X Large</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="xxl">
                          <ListboxLabel>XX Large</ListboxLabel>
                        </ListboxOption>
                        <ListboxOption value="xxxl">
                          <ListboxLabel>3X Large</ListboxLabel>
                        </ListboxOption>
                      </Listbox>
                    )}
                  />
                  {errors.tshirtSize && (
                    <ErrorMessage>
                      {errors.tshirtSize.message as string}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
            </div>
          </>
        </FormSection>

        {isPlayer && (
          <>
            <FormSection
              className="mt-1"
              formDescription="Provide details about your handicap and golf club membership."
              formHeading="Handicap and Golf Club Membership"
            >
              <div className="flex flex-col gap-4">
                <div className="col-span-3">
                  <Field>
                    <Label>Player Handicap</Label>
                    <Description>
                      Enter your current handicap index.
                    </Description>
                    <Input
                      type="number"
                      {...register("playerHandicap", {
                        required: "Player handicap is required",
                      })}
                    />
                    {errors.playerHandicap && (
                      <ErrorMessage>
                        {errors.playerHandicap.message as string}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>

                <div className="col-span-3">
                  <Field>
                    <Label>Home Club</Label>
                    <Description>Choose your home club.</Description>
                    <Controller
                      name="homeClub"
                      control={control}
                      rules={{ required: "Home Club is required" }}
                      render={({ field }) => (
                        <Listbox
                          name="homeClub"
                          value={field.value}
                          invalid={!!errors.homeClub}
                          onChange={(e) => setValue("homeClub", e)}
                        >
                          <ListboxOption value="HGA">
                            <ListboxLabel>
                              Hyderabad Golf Association
                            </ListboxLabel>
                          </ListboxOption>
                          <ListboxOption value="BoulderHills">
                            <ListboxLabel>
                              Boulder Hills, Hyderabad
                            </ListboxLabel>
                          </ListboxOption>
                          <ListboxOption value="Other">
                            <ListboxLabel>
                              Other club, enter club name.
                            </ListboxLabel>
                          </ListboxOption>
                        </Listbox>
                      )}
                    />
                    {errors.homeClub && (
                      <ErrorMessage>
                        {errors.homeClub?.message as string}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>

                {homeClub === "Other" && (
                  <div className="col-span-3">
                    <Field>
                      <Label>Home Club Name</Label>
                      <Description>
                        Enter the name of your home club.
                      </Description>
                      <Input
                        type="text"
                        {...register("homeClubName", {
                          required: "Home Club Name is required",
                        })}
                      />
                      {errors.homeClubName && (
                        <ErrorMessage>
                          {errors.homeClubName.message as string}
                        </ErrorMessage>
                      )}
                    </Field>
                  </div>
                )}
                {homeClub && homeClub !== "HGA" && (
                  <div className="col-span-3">
                    <Field>
                      <Label>Handicap Index Certificate</Label>
                      <Description>
                        Upload your handicap index certificate. Can be an image
                        or PDF.
                      </Description>
                      <div className="flex flex-row gap-4">
                        <input
                          type="file"
                          {...register("handicapCertificate")}
                          onChange={handleFileChangeHCICert}
                          accept="image/*,application/pdf"
                          style={{ height: "39px", width: "109px" }}
                          className="block w-full text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-500"
                        />
                        {filePreviewHCICert &&
                          selectedFileHCICert &&
                          (selectedFileHCICert?.type === "application/pdf" ? (
                            <embed
                              src={filePreviewHCICert}
                              type="application/pdf"
                              width="180px"
                              height="200px"
                            />
                          ) : (
                            <img
                              src={filePreviewHCICert}
                              alt="Handicap Certificate"
                              style={{ width: "180px", height: "200px" }}
                            />
                          ))}
                      </div>
                      {errors.handicapCertificate && (
                        <ErrorMessage>
                          {errors.handicapCertificate.message as string}
                        </ErrorMessage>
                      )}
                    </Field>
                  </div>
                )}
              </div>
            </FormSection>

            <FormSection
              className="mt-1"
              formDescription=" This is a team game and your participation in the, on and off course
          events will be appreciated by Team owners and the League. Apart from
          the listed tournament dates, make yourself available for
          practice rounds and get together with team members."
              formHeading="Available Match Dates"
            >
              <div className="flex flex-col gap-4">
                <div className="col-span-2">
                  <Field>
                    <Label>Select availability for matches</Label>
                    <Description>
                      Check all the dates you will be available to play matches.
                    </Description>

                    <Controller
                      name="preferredDates"
                      control={control}
                      render={({ field }) => (
                        <>
                          {currentSeason?.preferredDates?.sort().map((date) => (
                            <div key={date}>
                              <CheckboxField key={date}>
                                <Checkbox
                                  value={date}
                                  onChange={(checked) => {
                                    if (checked) {
                                      field.onChange([...field.value, date]);
                                    } else {
                                      field.onChange(
                                        field.value.filter(
                                          (d: string) => d !== date
                                        )
                                      );
                                    }
                                  }}
                                  checked={field.value?.includes(date)}
                                />
                                <Label>{showDate(date)}</Label>
                              </CheckboxField>
                            </div>
                          ))}
                          {currentSeason?.preferredDates &&
                            currentSeason?.preferredDates?.length > 1 && (
                              <CheckboxField>
                                <Checkbox
                                  value={"allDates"}
                                  onChange={(checked) => {
                                    if (checked) {
                                      field.onChange(
                                        currentSeason?.preferredDates
                                      );
                                    } else {
                                      field.onChange([]);
                                    }
                                  }}
                                  checked={
                                    field.value?.length ===
                                    currentSeason?.preferredDates?.length
                                  }
                                />
                                <Label>Available on all the above</Label>
                              </CheckboxField>
                            )}
                        </>
                      )}
                    />
                  </Field>
                </div>
              </div>
            </FormSection>
          </>
        )}

        <div className="mt-4 p-6 pl-3 flex flex-row justify-between bg-gray-100 dark:bg-gray-700 rounded-md text-gray-800 dark:text-gray-200 ">
          <p className="text-center">Best of luck for the season!</p>
          <div className="flex gap-4">
            <Button color="red" onClick={onCancel}>
              <XCircleIcon className="h-6 w-6" />
              Cancel
            </Button>
            <Button color="green" onClick={handleSubmit(onSubmit)}>
              <CheckIcon className="h-6 w-6" />
              Save
            </Button>
          </div>
        </div>

        {isBusy && <Busy busyMessage="" />}
      </form>
    </>
  );
};

export default RegisterSeason;
